import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import UberDirectConfigModal from "./UberDirectConfigModal";
import UberService from "../../../../api/uber";

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  hover: {
    scale: 1.02,
    boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
    transition: { duration: 0.3 },
  },
};

const UberDirectCard = () => {
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);

  // Fetch Uber Direct credentials on component mount
  useEffect(() => {
    fetchCredentials();
  }, []);

  // Function to fetch Uber credentials
  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const { data } = await UberService.getCredentials();

      if (data && data.length > 0) {
        const cred = data[0];
        setCredentials(cred);
        return validateCredentials(cred);
      } else {
        setCredentials(null);
      }
    } catch (error) {
      console.error("Error fetching Uber Direct credentials:", error);
      setCredentials(null);
    } finally {
      setLoading(false);
    }
  };

  // Function to validate Uber credentials
  const validateCredentials = async (credential) => {
    setValidating(true);

    if (!credential) {
      return;
    }

    try {
      await UberService.validateCredentials({ id: credential.id });

      const updatedCredentials = { ...credentials, valid: true };
      setCredentials(updatedCredentials);
    } catch (error) {
      console.error("Error validating Uber Direct credentials:", error);
      const updatedCredentials = { ...credentials, valid: false };
      setCredentials(updatedCredentials);
    } finally {
      setValidating(false);
    }
  };

  // Handle config modal opening/closing
  const handleOpenConfigModal = () => setConfigModalOpen(true);
  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    fetchCredentials(); // Refresh credentials after modal close
  };

  return (
    <>
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
      >
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            border: credentials?.valid
              ? "1px solid #4CAF50"
              : "1px solid #eeeeee",
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "#000000",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <DeliveryDiningIcon sx={{ color: "#FFFFFF", fontSize: 30 }} />
                </Box>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" component="div" fontWeight="bold">
                  Uber Direct
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Integração com serviço de entregas Uber Direct
                </Typography>
              </Grid>
              <Grid item>
                {loading ? (
                  <CircularProgress size={24} />
                ) : credentials ? (
                  credentials.valid ? (
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label="Configurado"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorOutlineIcon />}
                      label="Erro na Validação"
                      color="error"
                      variant="outlined"
                    />
                  )
                ) : (
                  <Chip
                    icon={<ErrorOutlineIcon />}
                    label="Não Configurado"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2">
                Com a integração do Uber Direct, você pode:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <Typography component="li" variant="body2">
                  Solicitar cotações de entrega em tempo real
                </Typography>
                <Typography component="li" variant="body2">
                  Rastrear entregas em tempo real
                </Typography>
                <Typography component="li" variant="body2">
                  Obter atualizações de status automáticas via webhooks
                </Typography>
                <Typography component="li" variant="body2">
                  Acesso à ampla rede de entregadores do Uber
                </Typography>
              </Box>
            </Box>

            {/* Status informação */}
            {credentials?.valid && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> Integração ativa com o Uber Direct
                </Typography>
              </Box>
            )}
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SettingsIcon />}
              onClick={handleOpenConfigModal}
              sx={{
                bgcolor: "#000000",
                "&:hover": { bgcolor: "#333333" },
              }}
            >
              {credentials ? "Editar Configuração" : "Configurar Integração"}
            </Button>
          </CardActions>
        </Card>
      </motion.div>

      <UberDirectConfigModal
        open={configModalOpen}
        onClose={handleCloseConfigModal}
        existingCredentials={credentials}
      />
    </>
  );
};

export default UberDirectCard;
